import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import {
  CaptureModeEnum,
  StatusOfShootingEnum,
} from 'src/models/Camera/Shooting/Shooting.model';
import { EyePropertyCode } from 'src/models/Eye.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useUiTranslation from 'src/hooks/useUiTranslation';

import { captureError } from 'src/utils/errorReporting';

import useHandleHttpRequestError from '../useHandleHttpRequestError';
import useSubmitSession from '../useSubmitSession';
import { useGetCurrentCaptureProcess } from './useGetCurrentCaptureProcess';

const useCaptureImage = () => {
  const {
    setLeftUrl,
    leftUrl,
    setRightUrl,
    rightUrl,
    setIsCapturing,
    cameraData,
    isCanceling,
    setIsCanceling,
    hasStarted,
    setHasStarted,
  } = useRemoteBoundStore();

  const { submitImage } = useSubmitSession();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const { tCommon, language } = useUiTranslation();

  const captureBothImages = useCallback(async () => {
    try {
      setLeftUrl('');
      setRightUrl('');
      setIsCapturing(true);
      await CameraConnection.getInstance().autoTakeImage();
    } catch (err) {
      captureError(err, 'Failed to capture both images');

      const errorMessage = tCommon('message.error.failedToCaptureBothImages');

      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  }, [setLeftUrl, setRightUrl, setIsCapturing, tCommon, language]);

  const captureLeftImage = useCallback(async () => {
    try {
      setLeftUrl('');
      setIsCapturing(true);
      await CameraConnection.getInstance().autoTakeImage({
        eyetype: CaptureModeEnum.LEFT_EYE,
      });
    } catch (err) {
      captureError(err, 'Failed to capture left image');

      const errorMessage = tCommon('message.error.failedToCaptureLeftImage');

      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  }, [setLeftUrl, setIsCapturing, tCommon, language]);

  const captureRightImage = useCallback(async () => {
    try {
      setRightUrl('');
      setIsCapturing(true);
      await CameraConnection.getInstance().autoTakeImage({
        eyetype: CaptureModeEnum.RIGHT_EYE,
      });
    } catch (err) {
      captureError(err, 'Failed to capture right image');

      const errorMessage = tCommon('message.error.failedToCaptureRightImage');
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  }, [setRightUrl, setIsCapturing, tCommon, language]);

  const [hasTimedOutReadImage, setHasTimedOutReadImage] = useState(false);

  const checkImageAvailability = useCallback(
    (
      imgUrl: string,
      onSuccess: (imgUrl: string) => void,
      onError: (errorMsg: string) => void,
    ) => {
      const maxAttempts = 30;
      let countAttempt = 1;

      const interval = setInterval(async () => {
        try {
          if (countAttempt > maxAttempts) {
            clearInterval(interval);

            if (!hasTimedOutReadImage) {
              setHasTimedOutReadImage(true);

              Sentry.captureMessage(
                `Failed to read the image file ${imgUrl} in ${maxAttempts} seconds.`,
              );

              return onError(
                tCommon('message.error.readImageFileTimeout', {
                  imgUrl,
                  maxAttempts,
                }),
              );
            }
          }

          countAttempt++;

          await axios.head(imgUrl);
          clearInterval(interval);
          return onSuccess(imgUrl);
        } catch (err) {
          if (
            axios.isAxiosError(err) &&
            err.response &&
            err.response.status !== 404
          ) {
            clearInterval(interval);

            captureError(err, `Failed to read the image file ${imgUrl}`);

            return onError(
              tCommon('message.error.failedToReadImageFile', {
                imgUrl,
              }),
            );
          }
        }
      }, 1000);
    },
    [hasTimedOutReadImage, tCommon],
  );

  const handleImageProcess = useCallback(
    (filename: string): Promise<string> => {
      const imgUrl = `http://${cameraData?.ip}:8901/media/${filename}`;

      return new Promise((resolve, reject) => {
        checkImageAvailability(
          imgUrl,
          (successUrl: string) => {
            resolve(successUrl);
          },
          (errorMsg: string) => {
            Api.alertBox({
              title: tCommon('bootbox.title.Error'),
              message: errorMsg,
              locale: getLocaleCode(language as Language),
              type: 'error',
            });
            reject(new Error(errorMsg));
          },
        );
      });
    },
    [cameraData?.ip, checkImageAvailability, language, tCommon],
  );

  const { currentCaptureProcess } = useGetCurrentCaptureProcess();

  const getImageUrl = useCallback(async () => {
    const { state, filename } = currentCaptureProcess ?? { filename: [] };

    switch (state) {
      case StatusOfShootingEnum.COMPLETE_LEFT: {
        try {
          if (leftUrl) {
            return;
          }
          const leftImgUrl = await handleImageProcess(filename[0]);

          const imageFileName = leftImgUrl.split('/').pop() || '';

          await submitImage({
            side: EyePropertyCode.LEFT,
            imageUrl: leftImgUrl,
            imageFileName,
            hasQualityPrecheck: false, // TODO: Change when precheck is implemented
          });
        } catch (error) {
          handleHttpRequestError(error);
        }
        break;
      }

      case StatusOfShootingEnum.COMPLETE_RIGHT: {
        try {
          if (rightUrl) {
            return;
          }

          const rightImgUrl = await handleImageProcess(filename[0]);

          const imageFileName = rightImgUrl.split('/').pop() || '';

          await submitImage({
            side: EyePropertyCode.RIGHT,
            imageUrl: rightImgUrl,
            imageFileName,
            hasQualityPrecheck: false, // TODO: Change when precheck is implemented
          });
        } catch (error) {
          handleHttpRequestError(error);
        }
        break;
      }

      case StatusOfShootingEnum.RUNNING: {
        setHasStarted(true);
        setHasTimedOutReadImage(false);
        break;
      }

      case StatusOfShootingEnum.COMPLETE: {
        // When the user click capture button after some image has been captured, the status is complete and then go to running status.
        if (hasStarted) {
          setIsCapturing(false);
          setHasStarted(false);
        }

        // When user click cancel button after some image has been captured.
        if (isCanceling) {
          setIsCanceling(false);
        }

        break;
      }

      case StatusOfShootingEnum.IDLE: {
        // When user click cancel button before any image has been captured.
        if (isCanceling) {
          setIsCapturing(false);
          setIsCanceling(false);
          setHasStarted(false);
        }

        break;
      }

      default: {
        break;
      }
    }
  }, [
    currentCaptureProcess,
    leftUrl,
    handleImageProcess,
    submitImage,
    handleHttpRequestError,
    rightUrl,
    setHasStarted,
    hasStarted,
    isCanceling,
    setIsCapturing,
    setIsCanceling,
  ]);

  useEffect(() => {
    getImageUrl();
  }, [getImageUrl]);

  return { captureBothImages, captureLeftImage, captureRightImage };
};

export default useCaptureImage;
